import React, { useEffect, useRef, useState } from "react";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ShieldAlert, Paperclip, Upload, X, FileText, Clock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";

const fmt = (s) => (s ? new Date(s).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

const deadlineText = (rb) => {
  if (!rb) return "Respond with your side of the story.";
  const d = new Date(rb).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  const days = Math.ceil((new Date(rb) - new Date()) / 86400000);
  return days > 0 ? `Respond by ${d} · ${days} day${days === 1 ? "" : "s"} left` : `Respond by ${d}`;
};

const DECISION_LABEL = {
  pending: "Under review",
  no_violation: "No violation found",
  warning_issued: "Warning issued",
  collector_suspended: "Account action taken",
};
const decisionCls = (d) =>
  d === "no_violation" ? "bg-[#2D6A4F]/15 text-[#2D6A4F]" :
  d === "warning_issued" ? "bg-[#E9C46A]/30 text-[#1E3F33]" :
  d === "collector_suspended" ? "bg-[#B91C1C]/10 text-[#B91C1C]" :
  "bg-[#1E3F33]/10 text-[#5C6B64]";

function RespondDialog({ report, onDone }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(report.response?.text || "");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef();

  const addFiles = (e) => {
    setFiles((prev) => [...prev, ...Array.from(e.target.files || [])]);
    if (inputRef.current) inputRef.current.value = "";
  };
  const removeFile = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) { toast.error("Please write your response"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("response_text", text);
      files.forEach((f) => fd.append("files", f));
      await api.post(`/reports/${report.id}/respond`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Response submitted — our team will review it.");
      setOpen(false);
      setFiles([]);
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button type="button" data-testid={`complaint-respond-open-${report.id}`} className="btn-primary text-xs py-2 mt-1">
          {report.collector_responded ? "Edit your response" : "Respond to this complaint"}
        </button>
      </DialogTrigger>
      <DialogContent data-testid="complaint-respond-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-[#1E3F33]">Your response</DialogTitle>
          <DialogDescription className="text-[#5C6B64]">Explain your side and attach any documents or pictures as proof. An admin will review both sides and decide the case.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <textarea data-testid="complaint-response-text" required rows={5} className="input-soft" placeholder="Write your response…" value={text} onChange={(e) => setText(e.target.value)} />
          <div>
            <input ref={inputRef} data-testid="complaint-response-file-input" type="file" multiple accept="application/pdf,image/*" onChange={addFiles} className="hidden" />
            <button type="button" data-testid="complaint-response-attach" onClick={() => inputRef.current?.click()} className="btn-secondary w-full inline-flex items-center justify-center gap-2 text-sm">
              <Upload className="w-4 h-4" /> Attach documents or pictures
            </button>
            {files.length > 0 && (
              <ul className="mt-3 space-y-1.5">
                {files.map((f, i) => (
                  <li key={i} className="flex items-center justify-between text-xs text-[#1E3F33]">
                    <span className="inline-flex items-center gap-1.5 truncate"><FileText className="w-3.5 h-3.5 shrink-0" /> {f.name}</span>
                    <button type="button" onClick={() => removeFile(i)} className="text-[#8A9A86] hover:text-[#B91C1C] shrink-0"><X className="w-3.5 h-3.5" /></button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <button type="submit" data-testid="complaint-response-submit" disabled={busy} className="btn-primary w-full">{busy ? "Submitting…" : "Submit response"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function CollectorComplaints() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const load = () => api.get("/reports/against-me").then(({ data }) => setItems(data)).catch(() => {}).finally(() => setLoaded(true));
  useEffect(() => { load(); }, []);

  if (!loaded || items.length === 0) return null;

  return (
    <div className="card-soft border-[#B91C1C]/20" data-testid="collector-complaints-card">
      <h2 className="font-heading text-xl text-[#1E3F33] mb-1 flex items-center gap-2">
        <ShieldAlert className="w-4 h-4 text-[#B91C1C]" /> Complaints about you ({items.length})
      </h2>
      <p className="text-xs text-[#8A9A86] mb-4">A customer raised a concern. Review the details and respond with your side — an admin decides the outcome.</p>
      <ul className="space-y-4">
        {items.map((r) => (
          <li key={r.id} className="border border-[#1E3F33]/10 rounded-xl p-4" data-testid={`complaint-${r.id}`}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-xs text-[#8A9A86]">Filed {fmt(r.created_at)}</div>
              <span className={`text-[10px] px-2 py-0.5 rounded-full ${decisionCls(r.decision)}`} data-testid={`complaint-decision-${r.id}`}>{DECISION_LABEL[r.decision] || "Under review"}</span>
            </div>
            <p className="text-sm text-[#1E3F33] mt-2 whitespace-pre-wrap">{r.explanation}</p>
            {(r.attachments || []).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {r.attachments.map((a, i) => (
                  <a key={i} href={`${API}/files/${a.file_path}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#CC5A3A] hover:underline border border-[#1E3F33]/10 rounded-lg px-2 py-1">
                    <Paperclip className="w-3 h-3" /> {a.original_name || `Proof ${i + 1}`}
                  </a>
                ))}
              </div>
            )}

            {r.decision_note && (
              <div className="mt-3 text-xs bg-[#F0EFEA] rounded-lg p-2.5 text-[#1E3F33]"><span className="font-medium">Admin note:</span> {r.decision_note}</div>
            )}

            {r.response ? (
              <div className="mt-3 border-t border-[#1E3F33]/10 pt-3">
                <div className="text-xs font-medium text-[#2D6A4F]">Your response · {fmt(r.response.responded_at)}</div>
                <p className="text-sm text-[#5C6B64] mt-1 whitespace-pre-wrap" data-testid={`complaint-my-response-${r.id}`}>{r.response.text}</p>
                {(r.response.attachments || []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {r.response.attachments.map((a, i) => (
                      <a key={i} href={`${API}/files/${a.file_path}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#CC5A3A] hover:underline border border-[#1E3F33]/10 rounded-lg px-2 py-1">
                        <Paperclip className="w-3 h-3" /> {a.original_name || `Proof ${i + 1}`}
                      </a>
                    ))}
                  </div>
                )}
                <div className="mt-2"><RespondDialog report={r} onDone={load} /></div>
              </div>
            ) : r.response_window_open ? (
              <div className="mt-3">
                <div className="text-xs text-[#8A9A86] mb-2 inline-flex items-center gap-1.5" data-testid={`complaint-deadline-${r.id}`}>
                  <Clock className="w-3.5 h-3.5" /> {deadlineText(r.respond_by)}
                </div>
                <RespondDialog report={r} onDone={load} />
              </div>
            ) : (
              <div className="mt-3 text-xs text-[#B91C1C] inline-flex items-center gap-1.5" data-testid={`complaint-window-closed-${r.id}`}>
                <Clock className="w-3.5 h-3.5" /> The 14-day window to respond has closed.
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
